import { ChangeDetectionStrategy, Component, inject, OnDestroy, signal } from '@angular/core';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Subscription } from 'rxjs';

import { environment } from '@env/environment';

import { CommonService } from '@shared/services/common.service';
import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';
import { AppSettingsService } from '@shared/services/settings/app-settings.service';

@Component({
  selector: 'app-bot-ui-config-settings',
  templateUrl: './bot-ui-config-settings.component.html',
  styleUrls: ['./bot-ui-config-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSlideToggle],
})
export class BotUiConfigSettingsComponent implements OnDestroy {
  private readonly _metadataService = inject(MetadataService);
  private readonly _commonService = inject(CommonService);
  private readonly _appSettingsService = inject(AppSettingsService);

  private readonly _subs: Subscription[] = [];

  /** Current toggle value shown in admin UI. */
  protected readonly botUiConfigEnabled = signal(false);

  /** When no explicit metadata exists, UI matches deployment `allowedFeatures` until the admin saves. */
  protected readonly deploymentFallbackEnabled = signal(
    (environment.allowedFeatures ?? []).includes('bot-ui-config')
  );

  constructor() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.BotUiConfigEnabled).subscribe({
        next: metadata => {
          const raw = metadata?.value;
          if (raw === 'true') {
            this.botUiConfigEnabled.set(true);
          } else if (raw === 'false') {
            this.botUiConfigEnabled.set(false);
          } else {
            this.botUiConfigEnabled.set(this.deploymentFallbackEnabled());
          }
        },
        error: () => {
          this.botUiConfigEnabled.set(this.deploymentFallbackEnabled());
        },
      })
    );
  }

  protected onToggleChange(event: MatSlideToggleChange): void {
    const checked = event.checked;
    this._subs.push(
      this._metadataService
        .set(MetadataSlugs.BotUiConfigEnabled, checked ? 'true' : 'false')
        .subscribe({
          next: () => {
            this.botUiConfigEnabled.set(checked);
            this._appSettingsService.applyBotUiConfigFromMetadata(checked);
            this._commonService.openSuccessSnackBar('Bot UI configuration setting updated.');
          },
          error: () => {
            event.source.checked = !checked;
            this._commonService.openErrorSnackBar('Could not update Bot UI configuration setting.');
          },
        })
    );
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
  }
}
